import { useCurrentUserDateTimeFormat, useCurrentUserTimezone } from '@ingenico/react-section-utils';
import { ListViewItem, useRenderDefaultColumHeader } from '@ingenico/react-ui/datagrid';
import { useUserDateTimeFormat } from '@ingenico/react-utils/hooks';
import { ALL_LNGS_NAMESPACES, LNGS_NAMESPACE_RESOURCES } from '@ingenico/util-i18n-config';
import { Box, IconButton, Tooltip } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-premium';
import { toString } from 'lodash-es';
import type { ReactNode } from 'react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ResourceIconCategoryFront, type ResourceListItemFront } from '../../domain/models/resources.model';
import type { UseResourcesListColumnsInput } from './ResourcesList.model';
import { getHumanizedFileSize10 } from '../../utils/fileSizeConversion.util';
import styles from './ResourcesList.module.scss';
import { IconCategory } from './components/IconCategory.ctrl';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import type { KebabMenuOption } from '@ingenico/react-ui/kebab-menu';
import { KebabMenu } from '@ingenico/react-ui/kebab-menu';
import { AvatarWithNameListCol } from '../avatar-with-name-list-col/AvatarWithNameListCol.ctrl';

interface ResourcesListColumnsOutput {
  readonly defaultAvailableColumns: string[];
  readonly columns: GridColDef[];
}

const PREVIEWABLE_ICON_CATEGORIES: readonly ResourceIconCategoryFront[] = [
  ResourceIconCategoryFront.IMAGE,
  ResourceIconCategoryFront.TEXT,
];

function canPreviewResource(resource: ResourceListItemFront): boolean {
  return PREVIEWABLE_ICON_CATEGORIES.includes(resource.iconCategory);
}

export function useResourcesListColumns({
  canDisplayActions,
  canDeleteResource,
  onDeleteResource,
  onDownloadResource,
  onPreviewResource,
  canEditResource,
  onClickEditResource,
}: UseResourcesListColumnsInput): ResourcesListColumnsOutput {
  const { t } = useTranslation(Object.values(LNGS_NAMESPACE_RESOURCES));
  const { defaultRenderColumnHeader } = useRenderDefaultColumHeader(t);
  const currentUserDateTimeFormat = useCurrentUserDateTimeFormat();
  const timezone = useCurrentUserTimezone();
  const { getUserDateAndTimeFormatWithTimezone } = useUserDateTimeFormat(currentUserDateTimeFormat);

  const renderName = useCallback((params: GridRenderCellParams<ResourceListItemFront>): ReactNode => {
    const iconCategory = params.row.iconCategory;

    return (
      <Box className={styles.resourceNameRow__box}>
        <IconCategory name={iconCategory} />
        <ListViewItem className={styles.resourceNameRow__text} primaryText={toString(params.row.name)} />
      </Box>
    );
  }, []);

  const renderCreationDate = useCallback(
    (params: GridRenderCellParams<ResourceListItemFront>): ReactNode => {
      const [date, time] = getUserDateAndTimeFormatWithTimezone({
        instant: params.row.creationDate,
        timezone,
      });
      return <ListViewItem primaryText={date} secondaryText={time} />;
    },
    [getUserDateAndTimeFormatWithTimezone, timezone],
  );
  const renderAvatarUser = useCallback((params: GridRenderCellParams<ResourceListItemFront>): ReactNode => {
    return <AvatarWithNameListCol name={toString(params.row.createdBy)} isColumnCell />;
  }, []);

  const renderSize = useCallback(
    (params: GridRenderCellParams<ResourceListItemFront>): ReactNode => {
      const size = params.row.sizeBytes;
      return <ListViewItem primaryText={getHumanizedFileSize10(size, t, 2)} />;
    },
    [t],
  );
  const renderCellValue = useCallback(
    (params: GridRenderCellParams): ReactNode => (
      <ListViewItem primaryText={t(params.value, { ns: ALL_LNGS_NAMESPACES.DEPLOYMENTS })} />
    ),
    [t],
  );

  const renderActionsCell = useCallback(
    (params: GridRenderCellParams<ResourceListItemFront>): ReactNode => {
      const { row } = params;

      const kebabOptions: KebabMenuOption[] = [
        ...(canEditResource?.(row)
          ? [
              {
                label: toString(t('button.edit', { ns: ALL_LNGS_NAMESPACES.COMMON })),
                icon: <EditOutlinedIcon fontSize="small" color="primary" />,
                onClick: () => {
                  onClickEditResource?.(row);
                },
                dataTestId: 'kebab-menu-edit-button',
              },
            ]
          : []),
        ...(canDeleteResource(row)
          ? [
              {
                label: toString(t('button.delete', { ns: ALL_LNGS_NAMESPACES.COMMON })),
                icon: <DeleteOutlineIcon fontSize="small" color="error" />,
                onClick: () => {
                  onDeleteResource(row);
                },
                dataTestId: 'kebab-menu-delete-button',
              },
            ]
          : []),
      ];

      const canPreview = canPreviewResource(row);
      const previewLabel = toString(t('button.preview', { ns: ALL_LNGS_NAMESPACES.COMMON }));

      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title={toString(t('button.download', { ns: ALL_LNGS_NAMESPACES.COMMON }))}>
            <IconButton
              size="small"
              aria-label={toString(t('button.download', { ns: ALL_LNGS_NAMESPACES.COMMON }))}
              onClick={(event) => {
                event.stopPropagation();
                onDownloadResource(row);
              }}
            >
              <DownloadOutlinedIcon color="primary" fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title={canPreview ? previewLabel : ''}>
            <IconButton
              size="small"
              data-testid="preview-action"
              aria-label={canPreview ? previewLabel : undefined}
              aria-hidden={!canPreview}
              onClick={
                canPreview
                  ? (event) => {
                      event.stopPropagation();
                      onPreviewResource(row);
                    }
                  : undefined
              }
              sx={{ visibility: canPreview ? 'visible' : 'hidden' }}
            >
              <VisibilityOutlinedIcon color="primary" fontSize="small" />
            </IconButton>
          </Tooltip>

          {kebabOptions.length > 0 && <KebabMenu options={kebabOptions} />}
        </Box>
      );
    },
    [
      canDeleteResource,
      canEditResource,
      onClickEditResource,
      onDeleteResource,
      onDownloadResource,
      onPreviewResource,
      t,
    ],
  );

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'name',
        renderHeader: defaultRenderColumnHeader('field.name', 'common'),
        headerName: t('field.name', { ns: ALL_LNGS_NAMESPACES.COMMON }),
        flex: 1,
        renderCell: renderName,
        filterable: false,
      },
      {
        field: 'description',
        renderHeader: defaultRenderColumnHeader('field.description', 'common'),
        headerName: t('field.description', { ns: ALL_LNGS_NAMESPACES.COMMON }),
        flex: 1,
        renderCell: renderCellValue,
        filterable: false,
      },
      {
        field: 'filename',
        renderHeader: defaultRenderColumnHeader('field.filename', LNGS_NAMESPACE_RESOURCES.DEFAULT),
        headerName: t('field.filename', { ns: LNGS_NAMESPACE_RESOURCES.DEFAULT }),
        flex: 1,

        renderCell: renderCellValue,
        filterable: false,
      },
      {
        field: 'organizationName',
        renderHeader: defaultRenderColumnHeader('field.organizationName', 'common'),
        headerName: t('field.organizationName', { ns: ALL_LNGS_NAMESPACES.COMMON }),
        flex: 1,

        renderCell: renderCellValue,
        filterable: false,
        sortable: false,
      },
      {
        field: 'extension',
        renderHeader: defaultRenderColumnHeader('field.type', 'common'),
        headerName: t('field.type', { ns: ALL_LNGS_NAMESPACES.COMMON }),
        flex: 1,

        renderCell: renderCellValue,
        filterable: false,
      },
      {
        field: 'creationDate',
        renderHeader: defaultRenderColumnHeader('field.creationDate', LNGS_NAMESPACE_RESOURCES.DEFAULT),
        headerName: t('field.creationDate', { ns: LNGS_NAMESPACE_RESOURCES.DEFAULT }),
        flex: 1,
        renderCell: renderCreationDate,
        filterable: false,
      },
      {
        field: 'createdBy',
        renderHeader: defaultRenderColumnHeader('field.createdBy', LNGS_NAMESPACE_RESOURCES.DEFAULT),
        headerName: t('field.createdBy', { ns: LNGS_NAMESPACE_RESOURCES.DEFAULT }),
        flex: 1,
        renderCell: renderAvatarUser,
        filterable: false,
        sortable: false,
      },
      {
        field: 'sizeBytes',
        renderHeader: defaultRenderColumnHeader('field.size', 'common'),
        headerName: t('field.size', { ns: ALL_LNGS_NAMESPACES.COMMON }),
        flex: 1,
        renderCell: renderSize,
        filterable: false,
      },
      ...(canDisplayActions
        ? [
            {
              field: 'actions',
              renderHeader: () => '',
              headerName: t('field.actions', { ns: ALL_LNGS_NAMESPACES.COMMON }),
              renderCell: renderActionsCell,
              hideable: false,
              sortable: false,
              disableColumnMenu: true,
              disableReorder: true,
              width: 136,
            },
          ]
        : []),
    ],
    [
      canDisplayActions,
      defaultRenderColumnHeader,
      renderActionsCell,
      renderAvatarUser,
      renderCellValue,
      renderCreationDate,
      renderName,
      renderSize,
      t,
    ],
  );

  const defaultAvailableColumns = useMemo(() => columns.map((col) => col.field), [columns]);

  return {
    defaultAvailableColumns,
    columns,
  };
}
